import { withErrorBoundary } from "@sentry/react";

import ErrorFallback from "@components/error-fallback/error-fallback";
import { sanitizeMarkdown } from "@helpers/string";

import {
  AssessmentResponseData,
  extractSourcesBlock,
  extractTextFromContent,
  getStreamingAssessmentResponseText,
  splitAssessmentResponseTags,
} from "../helpers";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import AskAIMessageSources from "./ask-ai-message-sources";
import AskAISuggestedAssessmentResponses, {
  AskAISuggestedAssessmentResponseStreaming,
} from "./ask-ai-suggested-assessment-responses";
import useStreamingText from "./use-streaming-text";

const AskAIMessageContentComponent = ({
  questionId,
  content,
  isStreaming = false,
  chatSessionId,
  messageId,
  canEditSuggestedAssessmentResponse = false,
  isAssessmentSession = false,
}: {
  questionId?: number | null;
  content: string;
  isStreaming?: boolean;
  chatSessionId?: number | null;
  messageId: string;
  canEditSuggestedAssessmentResponse?: boolean;
  isAssessmentSession?: boolean;
}) => {
  const streamedContent = useStreamingText(content, isStreaming);
  const fullText = extractTextFromContent(streamedContent.content);
  // Split off the model's <tf-sources> block so it renders as its own toggle
  // instead of inline markdown (and so a partial tag never flashes mid-stream).
  const { text, sources } = extractSourcesBlock(fullText);
  const isVisuallyStreaming = isStreaming || streamedContent.isAnimating;
  const segments = splitAssessmentResponseTags(
    isVisuallyStreaming ? sanitizeMarkdown(text) : text,
    { isStreaming: isVisuallyStreaming },
  );
  const streamingAssessmentResponseText = isVisuallyStreaming
    ? getStreamingAssessmentResponseText(text)
    : null;

  return (
    <div className="flex flex-col gap-4">
      {segments.map((segment, index) => {
        if (typeof segment === "string") {
          return segment ? (
            <div key={index} className="prose prose-sm max-w-none">
              <AskAIMessageMarkdown>{segment}</AskAIMessageMarkdown>
            </div>
          ) : null;
        }
        const assessment = segment as AssessmentResponseData;
        // The suggested-answer card is only meaningful when the chat is tied to an
        // assessment (where the user can insert it into a review). Outside that
        // context, render the draft as ordinary markdown instead of the card.
        if (!isAssessmentSession) {
          return assessment.suggestedResponse ? (
            <div key={index} className="prose prose-sm max-w-none">
              <AskAIMessageMarkdown>
                {assessment.suggestedResponse}
              </AskAIMessageMarkdown>
            </div>
          ) : null;
        }
        return (
          <AskAISuggestedAssessmentResponses
            key={`assessment-${index}`}
            suggestedAssessmentResponses={[
              {
                suggestedResponse: assessment.suggestedResponse,
                citations: assessment.citations,
                suggestionId: assessment.suggestionId,
              },
            ]}
            chatSessionId={chatSessionId || undefined}
            messageId={messageId}
            questionId={questionId}
            canBeEdited={canEditSuggestedAssessmentResponse}
          />
        );
      })}
      {isAssessmentSession && streamingAssessmentResponseText !== null && (
        <AskAISuggestedAssessmentResponseStreaming
          suggestedResponse={streamingAssessmentResponseText}
        />
      )}
      <AskAIMessageSources sources={sources} />
    </div>
  );
};

const AskAIMessageContent = withErrorBoundary(AskAIMessageContentComponent, {
  fallback: ErrorFallback,
});

export default AskAIMessageContent;
